import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui-custom/Card";
import Button from "../ui-custom/Button";
import { CheckCircle, Check, X, Eye, Clock, PlayCircle, AlertCircle } from "lucide-react";
import { PettyCashApprovalDetailDialog } from "./PettyCashApprovalDetailDialog";

function getInitials(name) {
  return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
}

function getAvatarColor(name) {
  const colors = [
    "bg-sm-accent/15 text-sm-accent",
    "bg-dark-green/15 text-dark-green",
    "bg-sm-secondary/15 text-sm-secondary",
    "bg-sm-primary/15 text-sm-primary",
    "bg-destructive/15 text-destructive",
  ];
  const index = name.charCodeAt(0) % colors.length;
  return colors[index];
}

const statusTabs = [
  { id: "PENDING", label: "Pending", icon: Clock, color: "text-sm-accent", bg: "bg-sm-accent/10" },
  { id: "APPROVED", label: "Approved", icon: CheckCircle, color: "text-dark-green", bg: "bg-dark-green/10" },
  { id: "EXECUTED", label: "Executed", icon: PlayCircle, color: "text-sm-primary", bg: "bg-sm-primary/10" },
  { id: "REJECTED", label: "Rejected", icon: AlertCircle, color: "text-destructive", bg: "bg-destructive/10" },
];

export function PettyCashApprovals({
  allApprovals = [],
  pendingAllocations = [],
  onApproveExpense,
  onRejectExpense,
  approvalActionLoadingById,
  onApproveAllocation,
  onRejectAllocation,
  loadingApprovals = false,
}) {
  const [activeTab, setActiveTab] = useState("PENDING");
  const [selectedPerson, setSelectedPerson] = useState("ALL");
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const personOptions = useMemo(() => {
    const names = new Set();
    allApprovals.forEach((item) => {
      if (item?.employeeName) names.add(item.employeeName);
    });
    pendingAllocations.forEach((allocation) => {
      if (allocation?.employeeName) names.add(allocation.employeeName);
    });
    return ["ALL", ...Array.from(names).sort((a, b) => a.localeCompare(b))];
  }, [allApprovals, pendingAllocations]);

  const filteredItems = useMemo(() => {
    return allApprovals.filter((item) => {
      const matchesStatus = item.statusType === activeTab;
      const matchesPerson = selectedPerson === "ALL" || item.employeeName === selectedPerson;
      return matchesStatus && matchesPerson;
    });
  }, [allApprovals, activeTab, selectedPerson]);

  const filteredAllocations = useMemo(() => {
    if (activeTab !== "PENDING") return [];
    if (selectedPerson === "ALL") return pendingAllocations;
    return pendingAllocations.filter((allocation) => allocation.employeeName === selectedPerson);
  }, [pendingAllocations, activeTab, selectedPerson]);

  const totalFiltered = filteredItems.length + filteredAllocations.length;

  const handleView = (item) => {
    setSelectedApproval(item);
    setIsDetailOpen(true);
  };

  const getStatusBadge = (status) => {
    const tab = statusTabs.find(t => t.id === status) || statusTabs[0];
    return (
      <span className={`inline-block px-2 py-0.5 ${tab.bg} ${tab.color} text-xs rounded-full font-medium`}>
        {tab.label}
      </span>
    );
  };

  return (
    <div className="space-y-4">
      {/* Tab Navigation */}
      <div className="flex flex-wrap gap-2 p-1 bg-muted/20 rounded-lg w-fit">
        {statusTabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          const count = allApprovals.filter(a => a.statusType === tab.id).length + (tab.id === "PENDING" ? pendingAllocations.length : 0);

          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all ${isActive
                  ? "bg-white text-primary shadow-sm"
                  : "text-muted-foreground hover:text-foreground hover:bg-white/50"
                }`}
            >
              <Icon className={`w-4 h-4 ${isActive ? tab.color : ""}`} />
              {tab.label}
              {count > 0 && (
                <span className={`ml-1 w-6 h-6 text-[10px] flex flex-row items-center justify-center rounded-full ${isActive ? tab.bg + " " + tab.color : "bg-muted text-muted-foreground"}`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-2">
              <div className={`p-1.5 rounded ${statusTabs.find(t => t.id === activeTab)?.bg}`}>
                {(() => {
                  const tabInfo = statusTabs.find(t => t.id === activeTab);
                  const Icon = tabInfo?.icon || Clock;
                  return <Icon className={`w-4 h-4 ${tabInfo?.color}`} />;
                })()}
              </div>
              <div>
                <CardTitle className="text-base font-semibold">
                  {statusTabs.find(t => t.id === activeTab)?.label} Approvals
                </CardTitle>
                <p className="text-xs text-text-gray">
                  {loadingApprovals ? "Refreshing items..." : `${totalFiltered} items found`}
                </p>
              </div>
            </div>
            <div className="w-full md:w-64">
              <label className="block text-xs text-text-gray mb-1">Filter by person</label>
              <select
                className="w-full h-9 rounded-md border border-sm-border bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-sm-primary/30"
                value={selectedPerson}
                onChange={(e) => setSelectedPerson(e.target.value)}
              >
                {personOptions.map((name) => (
                  <option key={name} value={name}>
                    {name === "ALL" ? "All Persons" : name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0 space-y-4">
          {loadingApprovals && totalFiltered === 0 ? (
            <div className="p-12 text-center">
              <h3 className="text-lg font-semibold mb-2 text-primary-text">Loading...</h3>
              <p className="text-text-gray">Fetching results for {activeTab.toLowerCase()} status.</p>
            </div>
          ) : totalFiltered === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-muted mx-auto mb-4 flex items-center justify-center">
                <Check className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-2 text-primary-text">No items here</h3>
              <p className="text-text-gray">Nothing found in {activeTab.toLowerCase()} status.</p>
            </div>
          ) : (
            <>
              {/* Items */}
              {filteredItems.map((item) => (
                <div key={item.id} className="border border-sm-border rounded-md p-3 bg-gray-bg/10 hover:bg-gray-bg/20 transition-colors mb-2">
                  {/* Top Row: Avatar, Title/Employee, and Amount/Status */}
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      {item.userpic ? (
                        <img
                          src={item.userpic}
                          alt={item.employeeName}
                          className="w-9 h-9 shrink-0 rounded-full object-cover"
                        />
                      ) : (
                        <div className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center text-xs font-semibold ${getAvatarColor(item.employeeName)}`}>
                          {getInitials(item.employeeName)}
                        </div>
                      )}
                      <div className="truncate">
                        <p className="font-semibold text-primary-text text-sm leading-tight truncate">{item.title}</p>
                        <p className="text-xs text-text-gray truncate">{item.employeeName} • {item.project}</p>
                      </div>
                    </div>

                    <div className="text-right shrink-0">
                      <p className="text-base font-bold text-sm-primary leading-tight">₹{item.amount.toLocaleString("en-IN")}</p>
                      <div className="scale-90 origin-right mt-0.5">
                        {getStatusBadge(item.statusType)}
                      </div>
                    </div>
                  </div>

                  {/* Middle Row: Inline Metadata & Remarks */}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 mb-3 text-xs border-t border-sm-border/50 pt-2">
                    <div className="flex gap-1">
                      <span className="text-text-gray font-medium">Category:</span>
                      <span className="text-primary-text">{item.category || item.paymentType || "N/A"}</span>
                    </div>
                    <div className="flex gap-1">
                      <span className="text-text-gray font-medium">Date:</span>
                      <span className="text-primary-text">{formatDate(item.date)}</span>
                    </div>
                    {item.attachment && (
                      <span className="text-sm-accent bg-sm-accent/10 px-1.5 py-0.5 rounded flex items-center gap-1">
                        <Eye className="w-3 h-3" /> Receipt
                      </span>
                    )}
                    {item.remarks && (
                      <span className="text-text-gray italic truncate max-w-[200px] border-l pl-2 border-sm-border">
                        "{item.remarks}"
                      </span>
                    )}
                  </div>

                  {/* Bottom Row: Actions */}
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 text-xs px-2"
                      onClick={() => handleView(item)}
                      disabled={Boolean(approvalActionLoadingById?.[item.id])}
                    >
                      <Eye className="w-3.5 h-3.5 mr-1" />
                      View
                    </Button>

                    {activeTab === "PENDING" && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 text-xs px-2 text-destructive hover:bg-destructive/10"
                          onClick={() => onRejectExpense(item.id)}
                          disabled={Boolean(approvalActionLoadingById?.[item.id])}
                        >
                          <X className="w-3.5 h-3.5 mr-1" />
                          Decline
                        </Button>
                        <Button
                          size="sm"
                          className="h-8 text-xs px-3 bg-dark-green text-white hover:bg-dark-green/90"
                          onClick={() => onApproveExpense(item.id)}
                          disabled={Boolean(approvalActionLoadingById?.[item.id])}
                        >
                          <Check className="w-3.5 h-3.5 mr-1" />
                          {approvalActionLoadingById?.[item.id] ? "..." : "Approve"}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}

              {/* Allocations (shown only in Pending for now) */}
              {filteredAllocations.map((allocation) => (
                <div key={allocation.id} className="border border-sm-border rounded-lg p-5 bg-gray-bg/20">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      {allocation.userpic ? (
                        <img
                          src={allocation.userpic}
                          alt={allocation.employeeName}
                          className="w-11 h-11 rounded-full object-cover"
                        />
                      ) : (
                        <div className={`w-11 h-11 rounded-full flex items-center justify-center text-sm font-medium ${getAvatarColor(allocation.employeeName)}`}>
                          {getInitials(allocation.employeeName)}
                        </div>
                      )}
                      <div>
                        <p className="font-semibold text-primary-text">Fund Allocation Request</p>
                        <p className="text-sm text-text-gray">{allocation.employeeName}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-sm-primary">₹{allocation.amount.toLocaleString("en-IN")}</p>
                      {getStatusBadge("PENDING")}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-text-gray mb-4">
                    <span>{allocation.project}</span>
                    <span>•</span>
                    <span>{formatDate(allocation.date)}</span>
                    <span>•</span>
                    <span>{allocation.source}</span>
                  </div>

                  {allocation.remarks && (
                    <div className="bg-gray-bg/50 rounded-lg p-3 mb-4 text-sm text-text-gray italic">
                      "{allocation.remarks}"
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleView({
                        ...allocation,
                        title: "Fund Allocation Request",
                        statusType: "PENDING",
                        _raw: allocation
                      })}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => onApproveAllocation(allocation.id)}
                      className="bg-dark-green text-white hover:bg-dark-green/90"
                    >
                      <Check className="w-4 h-4 mr-1" />
                      Approve
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onRejectAllocation(allocation.id)}
                      className="text-destructive hover:bg-destructive/10"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Decline
                    </Button>
                  </div>
                </div>
              ))}
            </>
          )}
        </CardContent>
      </Card>

      <PettyCashApprovalDetailDialog
        open={isDetailOpen}
        onOpenChange={setIsDetailOpen}
        approval={selectedApproval}
      />
    </div>
  );
}
